import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Button, LargeTopStepper, Paper, Pill, Stack, Typography} from 'views/components';
import {AccountTreeOutlined as WorkflowIcon} from 'views/icons';
import {translate} from 'views/module';

import WorkflowTimeline from 'views/scenes/loom_lead/detail/client_scenes/client_feed/workflows/WorkflowTimeline';
import {TONE_PILL_PROPS, humanizeKind} from 'views/scenes/loom_lead/detail/client_scenes/client_feed/workflows/workflow_status';
import {formatDateTime, translateOr} from 'views/scenes/loom_lead/detail/client_scenes/client_feed/format';
import {JOB_INVITATION_STEPS, describeJobInvitation, jobInvitationDetails} from './job_invitation_status';

const DetailCell = (props) => {
  const {label, value} = props;

  return (
    <Stack
      sx={{
        gap: '6px',
        minWidth: 0,
        backgroundColor: '#FFFFFF',
        // Each cell draws its share of the grid lines, so a short last row leaves white space
        // rather than the grey of a gap-coloured grid background.
        boxShadow: '0 0 0 0.5px #E8E4DE',
        px: 2,
        py: 1.5,
      }}
    >
      <Typography sx={{fontWeight: 500, fontSize: '11px', lineHeight: '14px', color: '#A8A49E'}}>
        {label}
      </Typography>
      <Typography sx={{fontWeight: 700, fontSize: '12px', lineHeight: '16px', color: '#000000', wordBreak: 'break-word'}}>
        {value || '-'}
      </Typography>
    </Stack>
  );
};
DetailCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// A job invitation workflow, as the lead page's WorkflowCard shows a lead intake. It has no Complete
// action: only a lead intake can be completed by hand.
const JobInvitationCard = (props) => {
  const {workflow} = props;
  const [showTimeline, setShowTimeline] = React.useState(false);

  const description = React.useMemo(() => describeJobInvitation(workflow), [workflow]);

  const stepLabels = JOB_INVITATION_STEPS.map((step) => {
    const labelKey = step === 'Finished' && description.finishedStep ? description.finishedStep : step;
    return translate(`caregiver.LoomCaregiverDetail.Workflows.Steps.${labelKey}`);
  });

  const details = jobInvitationDetails(workflow);

  // TEAM_REVIEW is expected, not a fault, so it gets a softer note than the attention reasons.
  const isInTeamReview = description.state === 'TEAM_REVIEW' && !description.attention.length;

  return (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          px: 2,
          py: 1,
          backgroundColor: 'grey.100',
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <WorkflowIcon sx={{mr: 1}} />
          <Typography variant='h6'>
            {translateOr(`caregiver.LoomCaregiverDetail.Workflows.Type.${workflow.workflowType}`, humanizeKind(workflow.workflowType))}
          </Typography>
        </Box>
        <Pill
          label={translate(`caregiver.LoomCaregiverDetail.Workflows.State.${description.state}`)}
          {...TONE_PILL_PROPS[description.tone]}
        />
      </Box>
      <Stack spacing={2} sx={{p: 2}}>
        <Stack direction='row' sx={{gap: '7px', flexWrap: 'wrap'}}>
          <Pill
            label={translate('loomLead.LoomLeadDetail.Workflows.Started', {value: formatDateTime(workflow.createdAt)})}
            variant='neutral'
          />
          <Pill
            label={translate('loomLead.LoomLeadDetail.Workflows.Updated', {value: formatDateTime(workflow.updatedAt)})}
            variant='neutral'
          />
          {
            workflow.completedAt &&
            <Pill
              label={translate('loomLead.LoomLeadDetail.Workflows.Completed', {value: formatDateTime(workflow.completedAt)})}
              variant='neutral'
            />
          }
        </Stack>
        {
          description.attention.map((reason) => (
            <Alert key={reason} severity='error'>
              <Typography sx={{fontSize: '13px', fontWeight: 500}}>
                {translate('loomLead.LoomLeadDetail.Workflows.Attention.Title')}
              </Typography>
              <Typography sx={{fontSize: '13px'}}>
                {translate(`loomLead.LoomLeadDetail.Workflows.Attention.${reason}`)}
              </Typography>
            </Alert>
          ))
        }
        {
          isInTeamReview &&
          <Alert severity='warning'>
            {translate('loomLead.LoomLeadDetail.Workflows.Attention.TeamReview')}
          </Alert>
        }
        <LargeTopStepper
          steps={JOB_INVITATION_STEPS.length}
          activeStep={description.activeStep}
          stepLabels={stepLabels}
        />
        <Box
          sx={{
            display: 'grid',
            border: '1px solid #E8E4DE',
            borderRadius: '12px',
            overflow: 'hidden',
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
          }}
        >
          {
            details.map((detail) => (
              <DetailCell
                key={detail.key}
                label={detail.label}
                value={detail.value}
              />
            ))
          }
        </Box>
        {
          workflow.timeline.length > 0 &&
          <Box>
            <Button
              variant='text'
              onClick={() => setShowTimeline((isShown) => !isShown)}
              sx={{textTransform: 'none', px: 0}}
            >
              {
                showTimeline ?
                  translate('loomLead.LoomLeadDetail.Workflows.Timeline.Hide') :
                  translate('loomLead.LoomLeadDetail.Workflows.Timeline.Show', {count: workflow.timeline.length})
              }
            </Button>
            {
              showTimeline &&
              <WorkflowTimeline timeline={workflow.timeline} />
            }
          </Box>
        }
      </Stack>
    </Paper>
  );
};
JobInvitationCard.propTypes = {
  workflow: PropTypes.object.isRequired,
};

export default React.memo(JobInvitationCard);
